/**
 * (NOTA IMPORTANTE nordic-pages): Debido a que estamos migrando a nordic-pages, este archivo sera reemplazado.
 * Si tienes que hacer algun cambio en este archivo es necesario que lo agregues tambien
 * en la siguiente ruta: "app/nordic-pages/vpp/ui-components/views/base/MshopsVppBase.js"
 */

import React from 'react';

import { bool, node, shape, string } from 'prop-types';
import serialize from 'serialize-javascript';
import MeliGA from 'nordic/analytics/meli-ga';
import MelidataTrack from 'nordic/melidata/melidata-track';
import Script from 'nordic/script';
import Style from 'nordic/style';
import get from 'lodash/get';
import { LazyHydrate as Hydrator } from 'nordic/lazy';
import { Head } from 'nordic/head';
import RedirectApp from 'redirect-app-web';
import { Qualtrics } from 'qualtrics-wrapper';
import { UserProvider } from '../../../../../components/context/user';
import FlashMessage from '../../../../../components/flash-message';
import { StaticPropsProvider } from '../../../../../components/context/static-props';
import { StoreProvider } from '../../../../../store/context';
import rootReducer from '../../../../../reducers/root';
import Schema from '../../../../../components/schema';
import TrackPageVariation from '../../../features/tracking';
import Metadata from '../../../features/metadata';
import Hotjar from '../../../../../components/hotjar';
import GoogleTagManager from '../../../../../components/gtm';
import { constants } from '../../../../../utils/constants';
import useMShopsTrackingInfo from '../../../../../hooks/use-mshops-tracking-info';
import { getEmbeddedData } from '../../../../helpers/getEmbeddedData';

import { AndesProvider } from '@andes/context';
import { SHORTS_NORDIC_VIEWER } from '../../../../../utils/video-js-constants';

/**
 * Vpp Component
 */
const Vpp = ({
  children,
  country,
  csrfToken,
  currentUser,
  deviceType,
  deviceDpr,
  initialState,
  isLegacyBrowser,
  lowEnd,
  translations,
  site,
  siteId,
  baseURL,
  flashMessage,
  hotjar,
  gtm,
  isIE,
  imagePrefix,
  openInApp,
  page,
  cpCookie,
  coachMarkCookie,
  isGuestMS,
}) => {
  const { QUALTRICS_ID } = constants;
  const { layout } = initialState;
  const staticProps = {
    layout,
    flashMessage,
    site,
    siteId,
    lowEnd,
    deviceType,
    deviceDpr,
    country,
    isIE,
    imagePrefix,
    page,
    cpCookie,
    coachMarkCookie,
    isGuestMS,
  };

  const melidataExperiments = get(initialState, 'components.track.melidata_event.experiments', {});
  useMShopsTrackingInfo(get(initialState, 'shopsDefaultTrackingData'));
  const eventData = get(initialState, 'components.track.melidata_event.event_data');
  initialState.components.track.melidata_event.event_data = { ...eventData, is_mshops: true };

  const hasClips = !!initialState?.components?.gallery?.clips?.shorts?.length;

  const countryAs = country.locale.replace('_', '-') || country.locale;

  return (
    <>
      {hasClips && (
        <Head>
          <link href={SHORTS_NORDIC_VIEWER.VIDEO_JS_STYLE} rel="stylesheet" />
          <Script src={SHORTS_NORDIC_VIEWER.VIDEO_JS_SCRIPT} />
        </Head>
      )}
      {openInApp.shouldRedirect && <RedirectApp {...openInApp} />}
      <Hydrator whenIdle>
        <Schema schemaData={initialState.schema} />
      </Hydrator>
      {flashMessage && flashMessage.text && <FlashMessage {...flashMessage} />}
      <div className="ui-pdp">
        <MeliGA
          section={get(initialState, 'components.track.analytics_event.section')}
          page={get(initialState, 'components.track.analytics_event.page')}
          business={get(initialState, 'components.track.analytics_event.custom_dimensions.business')}
          pageVertical={get(initialState, 'components.track.analytics_event.custom_dimensions.pageVertical')}
          pathFromRoot={get(initialState, 'components.track.analytics_event.custom_dimensions.pathFromRoot')}
          dimensions={get(initialState, 'components.track.analytics_event.custom_dimensions.customDimensions')}
        />
        <MelidataTrack
          path={get(initialState, 'components.track.melidata_event.path')}
          event_data={get(initialState, 'components.track.melidata_event.event_data')}
          experiments={melidataExperiments}
        />

        <Style href={`vpp-np.${deviceType}-mshops.css`} critical={deviceType === 'mobile'} />
        <Script inline on="now">
          {`
          window.requestIdleCallback =
            window.requestIdleCallback ||
            function(cb) {
                var start = Date.now();
                return setTimeout(function() {
                    cb({
                        didTimeout: false,
                        timeRemaining: function() {
                            return Math.max(0, 50 - (Date.now() - start));
                        },
                    });
                }, 1);
            };

          window.cancelIdleCallback =
            window.cancelIdleCallback ||
            function(id) {
                clearTimeout(id);
            };
          window.__PRELOADED_STATE__ =
            ${serialize(
              {
                translations,
                site,
                siteId,
                lowEnd,
                deviceType,
                deviceDpr,
                country,
                currentUser,
                initialState,
                csrfToken,
                baseURL,
                flashMessage,
                isIE,
                imagePrefix,
                openInApp,
                page,
                cpCookie,
                coachMarkCookie,
              },
              { isJSON: true },
            )};
        `}
        </Script>
        {isLegacyBrowser && <Script src="polyfill.js" />}
        {isIE && <Script src="polyfills-ie.js" />}
        <Script src={`vpp.${deviceType}-mshops.js`} />
        <Qualtrics interceptId={QUALTRICS_ID} embeddedData={getEmbeddedData(initialState)} />
        <Hotjar {...hotjar} extraData={get(initialState, 'components.track.hotjar_event')} />
        <GoogleTagManager {...gtm} dataLayer={get(initialState, 'components.track.gtm_event')} />
        <AndesProvider locale={countryAs} csrfToken={csrfToken}>
          <UserProvider value={currentUser}>
            <StaticPropsProvider value={staticProps}>
              <StoreProvider siteId={siteId} defaultState={initialState} reducer={rootReducer}>
                {children}
                <Hydrator whenIdle>
                  <TrackPageVariation />
                </Hydrator>
                <Hydrator whenIdle>
                  <Metadata />
                </Hydrator>
              </StoreProvider>
            </StaticPropsProvider>
          </UserProvider>
        </AndesProvider>
      </div>
    </>
  );
};

Vpp.propTypes = {
  baseURL: string.isRequired,
  children: node.isRequired,
  country: shape({
    decimal_separator: string.isRequired,
    thousands_separator: string.isRequired,
    locale: string.isRequired,
  }).isRequired,
  csrfToken: string.isRequired,
  currentUser: shape({
    nickname: string.isRequired,
  }),
  deviceType: string.isRequired,
  deviceDpr: string,
  flashMessage: string,
  gtm: shape(),
  hotjar: shape(),
  initialState: shape().isRequired,
  isLegacyBrowser: bool,
  lowEnd: bool.isRequired,
  site: string.isRequired,
  siteId: string.isRequired,
  translations: shape(),
  isIE: bool,
  imagePrefix: string,
  openInApp: shape({
    shouldRedirect: bool,
  }).isRequired,
  page: string,
  cpCookie: string,
  coachMarkCookie: string,
  isGuestMS: string,
};

Vpp.defaultProps = {
  currentUser: null,
  flashMessage: null,
  gtm: null,
  hotjar: null,
  isLegacyBrowser: true,
  translations: {},
  isIE: false,
  imagePrefix: null,
};

/**
 * Inject i18n context as props into VPP.
 */
export default Vpp;
